import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { LazyMotion, motion } from 'framer-motion'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CalendarDots } from '@phosphor-icons/react/dist/ssr/CalendarDots'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { Compass } from '@phosphor-icons/react/dist/ssr/Compass'
import { FileDoc } from '@phosphor-icons/react/dist/ssr/FileDoc'
import { FileText } from '@phosphor-icons/react/dist/ssr/FileText'
import { FlagBanner } from '@phosphor-icons/react/dist/ssr/FlagBanner'
import { Handshake } from '@phosphor-icons/react/dist/ssr/Handshake'
import { Question } from '@phosphor-icons/react/dist/ssr/Question'
import { ShieldCheck } from '@phosphor-icons/react/dist/ssr/ShieldCheck'
import { ShareNetwork } from '@phosphor-icons/react/dist/ssr/ShareNetwork'
import { Timer } from '@phosphor-icons/react/dist/ssr/Timer'
import promptfreePoster from '@/assets/moss-orb-terrarium-poster.avif'
import gritnordLogo from '@/assets/gritnord-logo.avif'
import goshaPortrait from '@/assets/portrait-gosha-front-80.avif'
import rainVaanaPortrait from '@/assets/rain-vaana-driving.avif'
import miraVossPortrait from '@/assets/mira-voss-portrait.avif'
import vellumgridLogo from '@/assets/vellumgrid-logo.avif'
import rocketIcon from '@/assets/rocket.avif'
import wavingHand from '@/assets/waving-hand.avif'
import wildflowerFieldPoster from '@/assets/wildflower-field-poster.avif'
import { EditorialQuote } from '@/components/article/EditorialQuote'
import { CaseStudyFaqAccordion } from '@/components/case-study/CaseStudyFaqAccordion'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import { copyContactEmail } from '@/lib/copyContactEmail'

const rainPathname = '/hello/rain-vaana-739284'
const anonymousPathname = '/hello/mira-voss-604218'
const promptfreeVideoUrl = 'https://assets.gosha.ee/bgs/moss-orb-terrarium_web.mp4'
const offerVideoUrl = 'https://assets.gosha.ee/bgs/wildflower-field_web.mp4'
const loadDomAnimation = () =>
  import('@/lib/motion-dom-animation').then((mod) => mod.domAnimation)
const HomeTestimonials = lazy(() =>
  import('@/components/home/HomeTestimonials').then((mod) => ({ default: mod.HomeTestimonials }))
)

const readAlsoSlugs = [
  'automated-outbound-outreach-workflow',
  'personalized-outreach-visuals-workflow',
  'local-first-case-study-publishing-workflow',
  'clip-flip-linkedin-visual-workflow',
]

const caseStudySlug = (cs: ReturnType<typeof getAllCaseStudies>[number]) =>
  cs.page.slug

type FollowUpProfile = {
  condensed?: boolean
  pathname: string
  firstName: string
  fullName: string
  company: string
  email: string
  portrait: string
  companyLogo?: string
  companyInitial: string
  heroContext: string
  whatHeardTitle: string
  companyDescription: string
  topic: string
  emailContext: string
  quote: string
}

const rainProfile: FollowUpProfile = {
  pathname: rainPathname,
  firstName: 'Rain',
  fullName: 'Rain Vääna',
  company: 'Gritnord',
  email: 'rain@gritnord.com',
  portrait: rainVaanaPortrait,
  companyLogo: gritnordLogo,
  companyInitial: 'G',
  heroContext:
    'this asset is based on our conversation about Gritnord, ICP discovery, 10+ channels, booked meetings, low AI adoption, and the question you left open: will buyers actually engage with a personalized follow-up asset?',
  whatHeardTitle: 'already owns the hard part before the meeting.',
  companyDescription:
    'a deep ICP discovery and meeting engine: find the right buyers, pick the right channels, and get the meeting booked.',
  topic: 'testing personalized follow-up assets',
  emailContext:
    'Gritnord’s ICP work, and your question about whether buyers will react to personalized follow-up',
  quote:
    "If there is something that you see is repeatable, I'm always interested in new products and things that help my business grow.",
}

const anonymousProfile: FollowUpProfile = {
  condensed: true,
  pathname: anonymousPathname,
  firstName: 'Mira',
  fullName: 'Mira Voss',
  company: 'Vellumgrid',
  email: 'mira@vellumgrid.example',
  portrait: miraVossPortrait,
  companyLogo: vellumgridLogo,
  companyInitial: 'V',
  heroContext:
    'this recaps our conversation about Vellumgrid’s long sales cycle and one question: can tailored follow-up keep every stakeholder aligned?',
  whatHeardTitle: 'maps the workflow.',
  companyDescription:
    'a platform that finds process gaps and automation opportunities.',
  topic: 'buyer-specific follow-up',
  emailContext: 'Vellumgrid’s workflow and the need to align buyers',
  quote: 'A clear next step is worth testing.',
}

const createBenefits = (company: string) => [
  {
    title: 'Instantly feels made for the customer',
    body: 'The buyer sees their company, their meeting context, and the exact topics they cared about before the asset asks for anything.',
    Icon: Timer,
  },
  {
    title: 'Useful after the meeting',
    body: `${company} creates the meeting. The asset helps preserve momentum after it: recap, next steps, proof, and context in one place.`,
    Icon: ShareNetwork,
  },
  {
    title: 'Built for human review',
    body: 'AI drafts the asset from the transcript and customer background. A human approves the final version before it reaches the buyer.',
    Icon: FileText,
  },
  {
    title: 'Easy to test',
    body: 'Start with one real conversation. Send one private asset. Measure opens, replies, feedback, and whether the same pattern is worth repeating.',
    Icon: ShieldCheck,
  },
]

const createPilotSteps = (company: string): ReactNode[] => [
  <>
    Pick <strong className="font-medium text-white">five real or anonymized {company} meetings</strong>{' '}
    where the next step matters.
  </>,
  <>
    Gosha will create <strong className="font-medium text-white">one private asset</strong> for each
    final {company} customer <strong className="font-medium text-white">within 2 days</strong> after
    the call.
  </>,
  <>
    <strong className="font-medium text-white">Review every asset manually</strong> before sending.
    Keep the salesperson in control.
  </>,
  <>
    Judge the tests by <strong className="font-medium text-white">opens, replies, feedback</strong>,
    and whether the pattern feels repeatable.
  </>,
]

const createFaqItems = (company: string) => [
  {
    question: `Where could this fit ${company}?`,
    answer: `Right after a qualified meeting, where momentum is easiest to lose. ${company} already creates the opportunity; this gives the buyer and the customer a concrete asset to continue the decision.`,
  },
  {
    question: 'Will people actually open a personalized follow-up asset?',
    answer:
      'They are more likely to open it when the asset clearly gives them something a short message cannot: tailored context, relevant proof, next steps, and answers framed around their own situation.',
  },
  {
    question: 'What goes into the asset?',
    answer:
      'Transcript, customer background, meeting notes, product materials, public company context, and the seller’s next-step logic. The goal is not to dump everything in; the goal is to shape the useful parts into something the buyer can act on.',
  },
  {
    question: 'Who creates the asset?',
    answer:
      'The salesperson creates the asset with a series of automations built by Gosha. Gosha sets up the system, section kit, prompts, and workflow; sales uses it to generate buyer-specific assets without waiting on a designer or developer each time.',
  },
  {
    question: 'Is the asset built in the company’s visual style?',
    answer:
      'Yes. The asset should feel like it belongs to the company sending it. Even this page is built in the style of my own portfolio: typography, spacing, buttons, visuals, and interaction patterns all match the existing system.',
  },
  {
    question: 'Why should this be a private asset?',
    answer:
      'Because the useful version mentions the real company, the actual conversation, the buyer’s questions, and the offer angle for them. That is too specific for public marketing, so the asset should stay noindex and hard to guess.',
  },
  {
    question: 'Is this one-to-one or one-to-many?',
    answer:
      'For this use case, one-to-one. A one-to-many landing page can work for SEO or a niche campaign. This asset is different: it is made for one buyer after one serious conversation.',
  },
  {
    question: 'Does this depend on email?',
    answer:
      `No. ${company} can send the link through the channel that fits the buyer: LinkedIn, WhatsApp, Slack, calendar follow-up, or whatever sequence already works. The follow-up asset sits behind the message.`,
  },
  {
    question: 'Could hyperpersonalization feel too much?',
    answer:
      'Bad personalization feels like surveillance. Good personalization feels like preparation. The difference is usefulness: do not show off what you know, show the buyer why the conversation matters for their company.',
  },
  {
    question: `How much effort would ${company} need for a pilot?`,
    answer:
      'Very little. Use one real or anonymized meeting, one customer context, and one reviewed asset. If it is useful, the next step is process design. If it is not useful, the test stays small.',
  },
  {
    question: 'How should the pilot be judged?',
    answer:
      `Judge it by commercial behavior, not taste: would sales send it, would the buyer open it, did it create a reply, and can the same pattern work across more ${company} meetings?`,
  },
  {
    question: 'Does Gosha work alone?',
    answer:
      'Mostly, yes. I handle strategy, design, engineering, copy structure, and the automation setup myself. If a project needs extra manual production, I bring in trusted help for that specific part.',
  },
]

const condensedBenefits = [
  {
    title: 'Made for the buyer',
    body: 'The page starts with their company and call context.',
    Icon: Timer,
  },
  {
    title: 'Keeps momentum',
    body: 'One recap holds the proof and next step together.',
    Icon: ShareNetwork,
  },
  {
    title: 'Human-reviewed',
    body: 'AI drafts; a person checks every page.',
    Icon: FileText,
  },
  {
    title: 'Easy to test',
    body: 'Send one page, then track opens and replies.',
    Icon: ShieldCheck,
  },
]

const condensedPilotSteps: ReactNode[] = [
  <>
    Pick <strong className="font-medium text-white">five relevant meetings</strong>.
  </>,
  <>
    Build <strong className="font-medium text-white">one private page</strong> within two days.
  </>,
  <>
    <strong className="font-medium text-white">Review it</strong> before sending.
  </>,
  <>
    Track <strong className="font-medium text-white">opens, replies, and repeatability</strong>.
  </>,
]

const condensedFaqItems = [
  {
    question: 'Where does this fit?',
    answer: 'After a qualified meeting, when momentum is easiest to lose.',
  },
  {
    question: 'Will buyers open it?',
    answer: 'Yes—when it adds tailored context, proof, and a clear next step.',
  },
  {
    question: 'What goes in?',
    answer: 'The transcript, buyer context, useful proof, and the next step.',
  },
  {
    question: 'Who builds it?',
    answer: 'Sales generates it from a system I design and review.',
  },
  {
    question: 'Does it match the brand?',
    answer: 'Yes. Typography, layout, visuals, and interactions follow the sender’s brand.',
  },
  {
    question: 'Why keep it private?',
    answer: 'It includes real call context, so it stays noindex and hard to guess.',
  },
  {
    question: 'One-to-one or one-to-many?',
    answer: 'One-to-one: one page for one buyer after one real conversation.',
  },
  {
    question: 'Email only?',
    answer: 'No. Send it through any channel the buyer already uses.',
  },
  {
    question: 'Can personalization feel invasive?',
    answer: 'Only when it shows off data. Useful personalization feels prepared.',
  },
  {
    question: 'What does a pilot need?',
    answer: 'One meeting, one buyer context, and one reviewed page.',
  },
  {
    question: 'How do we judge it?',
    answer: 'Track opens, replies, useful feedback, and repeatability.',
  },
  {
    question: 'Do you work alone?',
    answer: 'Mostly. I lead strategy, design, engineering, copy, and automation.',
  },
]

const sectionTitleClass =
  "font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-[var(--foreground)]"

const darkPrimaryCtaClass =
  'inline-flex w-fit shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-white px-4 py-2 text-[15px] font-medium tracking-[-0.15px] text-black [transition-property:background-color,transform] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:bg-white/92 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black'

const offerInlineIconClass = 'ml-1 inline-block size-4 -translate-y-px text-white/45'

const SectionHeader = ({ eyebrow, title }: { eyebrow?: string; title: ReactNode }) => (
  <div className="space-y-1">
    {eyebrow ? (
      <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-[var(--foreground-subtle)]">
        {eyebrow}
      </div>
    ) : null}
    <h2 className={sectionTitleClass}>{title}</h2>
  </div>
)

const Section = ({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string
  title: ReactNode
  children: ReactNode
}) => (
  <section className="space-y-4">
    <SectionHeader eyebrow={eyebrow} title={title} />
    {children}
  </section>
)

const CompanyStackMark = ({ profile }: { profile: FollowUpProfile }) =>
  profile.companyLogo ? (
    <img
      src={profile.companyLogo}
      alt=""
      width={24}
      height={24}
      className="size-6 object-contain"
      decoding="async"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex size-6 items-center justify-center rounded-[5px] border border-white/20 bg-white/10 font-['Familjen_Grotesk'] text-[13px] text-white"
    >
      {profile.companyInitial}
    </span>
  )

const CompanyInlineMark = ({ profile }: { profile: FollowUpProfile }) =>
  profile.companyLogo ? (
    <img
      src={profile.companyLogo}
      alt=""
      width={24}
      height={24}
      className="mr-1 inline-block size-6 -translate-y-0.5 rounded-[5px] bg-black p-1"
      aria-hidden="true"
      decoding="async"
    />
  ) : (
    <span
      aria-hidden="true"
      className="mr-1 inline-flex size-6 -translate-y-0.5 items-center justify-center rounded-[5px] bg-black font-['Familjen_Grotesk'] text-[12px] text-white"
    >
      {profile.companyInitial}
    </span>
  )

const FollowUpPromptPromo = ({
  onCopyEmail,
  emailCopied,
  condensed = false,
}: {
  onCopyEmail: () => void
  emailCopied: boolean
  condensed?: boolean
}) => (
  <section aria-label="Personalized follow-up asset example">
    <div className="relative min-h-[133px] overflow-hidden rounded-[16px] bg-black px-4 py-3 text-white">
      <div className="pointer-events-none absolute top-0 left-[240px] h-full w-[239px] overflow-hidden">
        <img
          src={promptfreePoster}
          alt=""
          width={239}
          height={134}
          className="h-full w-full max-w-none scale-[1.35] object-cover md:hidden"
          decoding="async"
        />
        <video
          className="hidden h-full w-full max-w-none scale-[1.35] object-cover md:block"
          src={promptfreeVideoUrl}
          poster={promptfreePoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="absolute inset-y-0 left-0 w-[131px] bg-gradient-to-r from-black to-transparent" />
      </div>

      <div className="relative z-10 flex max-w-[314px] flex-col items-start gap-[18px]">
        <p className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[23.76px] tracking-[-0.44px] text-white [text-wrap:pretty]">
          {condensed ? 'Turn one call into a buyer page' : 'One call transcript becomes a buyer-specific asset'}
        </p>
        <p className="flex flex-nowrap items-center gap-x-2 text-[15px] leading-[22.5px] tracking-[-0.16px]">
          <a
            href={siteConfig.calendlyUrl}
            rel="noreferrer"
            target="_blank"
            className={darkPrimaryCtaClass}
            aria-label="Schedule a call (opens in new tab)"
          >
            Schedule a call
          </a>
          <span className="inline-flex items-center gap-x-2 whitespace-nowrap">
            <span className="font-medium text-white/55">or</span>
            <button
              type="button"
              onClick={onCopyEmail}
              className="group inline-flex items-center gap-1 font-medium text-white underline decoration-white/45 underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
            >
              copy email
              <span className="relative inline-flex size-5 items-center justify-center">
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 0 : 1,
                    scale: emailCopied ? 0.25 : 1,
                    filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <CopySimple size={16} weight="fill" className="text-white" />
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                    <CopySimple size={16} weight="regular" className="text-white" />
                  </span>
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 1 : 0,
                    scale: emailCopied ? 1 : 0.25,
                    filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <Check size={16} weight="bold" className="text-white" />
                </motion.span>
              </span>
            </button>
          </span>
        </p>
      </div>
    </div>
  </section>
)

export const RainVaanaFollowUpPage = ({
  profile = rainProfile,
}: {
  profile?: FollowUpProfile
}) => {
  const [emailCopied, setEmailCopied] = useState(false)
  const [scheduleLinkHovered, setScheduleLinkHovered] = useState(false)
  const [avatarHovered, setAvatarHovered] = useState(false)
  const [avatarSwapped, setAvatarSwapped] = useState(false)
  const resetTimerRef = useRef<number | null>(null)
  const readAlsoCaseStudies = readAlsoSlugs
    .map((slug) => getAllCaseStudies().find((cs) => caseStudySlug(cs) === slug))
    .filter((cs): cs is ReturnType<typeof getAllCaseStudies>[number] => Boolean(cs))
  const readAlsoNotes = getAllNotes().slice(0, 2)
  const benefits = profile.condensed ? condensedBenefits : createBenefits(profile.company)
  const pilotSteps = profile.condensed ? condensedPilotSteps : createPilotSteps(profile.company)
  const faqItems = profile.condensed ? condensedFaqItems : createFaqItems(profile.company)

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current)
      }
    },
    [],
  )

  const handleCopyEmail = async () => {
    const ok = await copyContactEmail()
    if (!ok) {
      return
    }
    setEmailCopied(true)
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current)
    }
    resetTimerRef.current = window.setTimeout(() => {
      setEmailCopied(false)
      resetTimerRef.current = null
    }, 1800)
  }

  return (
    <>
      <SeoHead pathname={profile.pathname} kind="private-follow-up" />
      <div className="site-page mx-auto w-full max-w-[448px] px-4 pb-24 md:pt-28">
        <div className="space-y-14">
          <header className="space-y-6">
            <motion.button
              type="button"
              aria-label={`Swap ${profile.firstName} portrait and ${profile.company} logo`}
              onClick={() => setAvatarSwapped((current) => !current)}
              onMouseEnter={() => setAvatarHovered(true)}
              onMouseLeave={() => setAvatarHovered(false)}
              onFocus={() => setAvatarHovered(true)}
              onBlur={() => setAvatarHovered(false)}
              whileTap={{ scale: 0.96 }}
              className="inline-flex h-[60px] w-10 cursor-pointer items-center justify-center self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            >
              <div className="relative h-[60px] w-10">
                <motion.img
                  src={profile.portrait}
                  alt={profile.fullName}
                  width={40}
                  height={60}
                  animate={{
                    x: avatarSwapped ? 3 : 0,
                    y: avatarSwapped ? 3 : 0,
                    scale: avatarSwapped ? 0.94 : 1,
                    rotate: avatarSwapped ? (avatarHovered ? 6 : 2) : avatarHovered ? -6 : -1,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 z-20 h-full w-full rounded-[6px] object-cover shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                  decoding="async"
                />
                <motion.span
                  aria-hidden
                  animate={{
                    x: avatarSwapped ? 0 : 3,
                    y: avatarSwapped ? 0 : 3,
                    scale: avatarSwapped ? 1 : 0.94,
                    rotate: avatarSwapped ? (avatarHovered ? -6 : -1) : avatarHovered ? 6 : 2,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 flex h-full w-full items-center justify-center rounded-[6px] bg-black p-2 shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                  style={{ zIndex: avatarSwapped ? 20 : 10 }}
                >
                  <CompanyStackMark profile={profile} />
                </motion.span>
              </div>
            </motion.button>
            <div className="space-y-4">
              <h1 className="ui-heading-balance font-['Familjen_Grotesk'] text-[32px] font-normal leading-[1.02] tracking-[-0.32px] text-[var(--foreground)]">
                <img
                  src={wavingHand}
                  alt=""
                  width={32}
                  height={32}
                  className="rain-wave-icon mr-2 inline-block size-8"
                  aria-hidden="true"
                  decoding="async"
                />
                {profile.condensed
                  ? `${profile.firstName}, here’s the follow-up.`
                  : `${profile.firstName}! Thanks for the call. Here is the follow-up`}
              </h1>
              <p className="text-[15px] leading-6 tracking-[-0.01em] text-[var(--foreground-muted)] [text-wrap:pretty]">
                {profile.firstName}, {profile.heroContext}
              </p>
            </div>
            <p className="text-[15px] leading-6 tracking-[-0.01em] text-[var(--foreground-muted)] [text-wrap:pretty]">
              {profile.condensed ? 'Want to continue? ' : 'If you want to chat again, you can '}
              <a
                href={siteConfig.calendlyUrl}
                rel="noreferrer"
                target="_blank"
                onMouseEnter={() => setScheduleLinkHovered(true)}
                onMouseLeave={() => setScheduleLinkHovered(false)}
                onFocus={() => setScheduleLinkHovered(true)}
                onBlur={() => setScheduleLinkHovered(false)}
                aria-label="Book next call (opens in new tab)"
                className="inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                book next call
                <span className="relative inline-flex size-4 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 0 : 1,
                      scale: scheduleLinkHovered ? 0.25 : 1,
                      filter: scheduleLinkHovered ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <CalendarDots aria-hidden size={16} weight="regular" />
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 1 : 0,
                      scale: scheduleLinkHovered ? 1 : 0.25,
                      filter: scheduleLinkHovered ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <ArrowUpRight aria-hidden size={16} weight="regular" />
                  </motion.span>
                </span>
              </a>
              {profile.condensed ? ' or ' : ' or here is my '}
              <button
                type="button"
                onClick={handleCopyEmail}
                aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
                className="group inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                email
                <span className="relative inline-flex size-5 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 0 : 1,
                      scale: emailCopied ? 0.25 : 1,
                      filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                      <CopySimple size={16} weight="fill" />
                    </span>
                    <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                      <CopySimple size={16} weight="regular" />
                    </span>
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 1 : 0,
                      scale: emailCopied ? 1 : 0.25,
                      filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Check size={16} weight="bold" />
                  </motion.span>
                </span>
              </button>
              .
            </p>
          </header>

          <Section
            eyebrow="What I heard"
            title={
              <>
                <CompanyInlineMark profile={profile} />
                {profile.company} {profile.whatHeardTitle}
              </>
            }
          >
            <div className="space-y-4 text-[15px] leading-6 text-[var(--foreground-muted)] [text-wrap:pretty]">
              <p>
                You described {profile.company} as {profile.companyDescription}
              </p>
              <p>
                {profile.condensed ? (
                  <>
                    The gap starts <strong className="font-medium text-[var(--foreground)]">after the call</strong>.
                    A tailored page keeps the decision clear and shareable.
                  </>
                ) : (
                  <>
                    The gap is what happens{' '}
                    <strong className="font-medium text-[var(--foreground)]">after a serious conversation</strong>.
                    A tailored asset can turn notes, materials, and next steps into a{' '}
                    <strong className="font-medium text-[var(--foreground)]">sales asset</strong> the
                    buyer can open, share, and react to.
                  </>
                )}
              </p>
            </div>
          </Section>

          <section className="overflow-hidden rounded-[16px] bg-black text-white">
            <div className="space-y-4 px-4 pt-4 pb-0">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-white/45">
                  Solution
                </div>
                <h2 className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-white [text-wrap:pretty]">
                  {profile.condensed
                    ? 'Turn one meeting into a clear next step.'
                    : 'Turn one serious meeting into a sales asset around its context.'}
                </h2>
              </div>
              <div className="rounded-[10px] border border-white/12 bg-white/7 text-[14px] leading-[21px] text-white/68">
                <div className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">to:</span>
                    <img
                      src={profile.portrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>{profile.email}</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">from:</span>
                    <img
                      src={goshaPortrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>gknjazev@gmail.com</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <span className="font-medium text-white/42">topic:</span>{' '}
                    <span>{profile.topic}</span>
                  </div>
                </div>
                <div className="border-t border-white/10 p-4 text-[15px] leading-6 [text-wrap:pretty]">
                  <p>
                    {profile.condensed
                      ? `${profile.firstName}, here’s the promised page. It reflects ${profile.emailContext}: `
                      : `${profile.firstName}, here is the asset I promised. I based it on our conversation, ${profile.emailContext}: `}
                    <a
                      href={profile.pathname}
                      className="font-medium text-white underline decoration-white/45 underline-offset-3 transition-[text-decoration-color] duration-150 hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    >
                      open it
                    </a>
                    .
                  </p>
                </div>
              </div>
              <p className="text-[15px] font-medium leading-6 text-white [text-wrap:pretty]">
                {profile.condensed ? (
                  <>
                    Give each buyer one page with proof
                    <ShieldCheck className={offerInlineIconClass} weight="regular" aria-hidden />, answers
                    <Question className={offerInlineIconClass} weight="regular" aria-hidden />, and next steps
                    <FlagBanner className={offerInlineIconClass} weight="regular" aria-hidden />.
                  </>
                ) : (
                  <>
                    Start building a customer-specific environment that creates the wow effect: your positioning
                    <Compass className={offerInlineIconClass} weight="regular" aria-hidden />, proofs
                    <ShieldCheck className={offerInlineIconClass} weight="regular" aria-hidden />, next
                    steps
                    <FlagBanner className={offerInlineIconClass} weight="regular" aria-hidden />,
                    offers
                    <Handshake className={offerInlineIconClass} weight="regular" aria-hidden />, answers
                    <Question className={offerInlineIconClass} weight="regular" aria-hidden />, docs
                    <FileDoc className={offerInlineIconClass} weight="regular" aria-hidden />, and the
                    exact context from the meeting, all shaped around one buyer instead of a generic
                    follow-up.
                  </>
                )}
              </p>
            </div>
            <div className="relative h-[176px] overflow-hidden md:h-[220px]">
              <img
                src={wildflowerFieldPoster}
                alt=""
                width={960}
                height={540}
                className="h-full w-full object-cover md:hidden"
                aria-hidden="true"
                decoding="async"
              />
              <video
                src={offerVideoUrl}
                poster={wildflowerFieldPoster}
                className="hidden h-full w-full object-cover md:block"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black via-black/55 to-transparent" />
            </div>
          </section>

          <Section
            eyebrow="Benefits"
            title={profile.condensed ? 'Why it works after the call.' : 'Why this can work after a sales call.'}
          >
            <div className="grid grid-cols-2 gap-2">
              {benefits.map((benefit) => (
                <article key={benefit.title} className="rounded-[10px] border border-[var(--border)] bg-white p-4">
                  <benefit.Icon
                    aria-hidden
                    className="mb-3 size-8 text-[var(--foreground-subtle)]"
                    weight="regular"
                  />
                  <h3 className="font-['Familjen_Grotesk'] text-[18px] font-normal leading-6 tracking-[-0.16px] text-[var(--foreground)]">
                    {benefit.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[22px] text-[var(--foreground-muted)] [text-wrap:pretty]">
                    {benefit.body}
                  </p>
                </article>
              ))}
            </div>
          </Section>

          <div className="py-2">
            <EditorialQuote
              body={profile.quote}
              name={profile.fullName}
              role={profile.company}
              photoUrl={profile.portrait}
            />
          </div>

          <Section
            eyebrow="Pilot"
            title={
              <>
                <img
                  src={rocketIcon}
                  alt=""
                  width={24}
                  height={24}
                  className="rocket-shake-icon mr-2 inline-block size-6"
                  aria-hidden="true"
                  decoding="async"
                />
                {profile.condensed
                  ? 'Test five follow-ups first.'
                  : `Start with five ${profile.company} follow-up tests, not an integration project.`}
              </>
            }
          >
            <div className="space-y-3">
              {pilotSteps.map((item, index) => (
                <div key={index} className="flex items-center gap-3 rounded-[10px] border border-white/10 bg-black p-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-semibold leading-none text-black">
                    {index + 1}
                  </span>
                  <p className="text-[14px] leading-[22px] text-white/72">{item}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title={profile.condensed ? 'Questions.' : 'Questions from the call.'}>
            <CaseStudyFaqAccordion items={faqItems} />
          </Section>

          <FollowUpPromptPromo
            onCopyEmail={handleCopyEmail}
            emailCopied={emailCopied}
            condensed={profile.condensed}
          />

          <div className="-mx-4">
            <LazyMotion features={loadDomAnimation} strict>
              <Suspense fallback={null}>
                <HomeTestimonials />
              </Suspense>
            </LazyMotion>
          </div>

          <HomeAiCaseStudies
            title="Read also"
            items={readAlsoCaseStudies}
            notes={readAlsoNotes}
            showExploreMore={false}
            padded={false}
            variant="readAlso"
          />
        </div>
      </div>
    </>
  )
}

export const AnonymousFollowUpPage = () => <RainVaanaFollowUpPage profile={anonymousProfile} />
